"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.adjustmentSchema = void 0;
var _zod = require("zod");
var _commonUtils = require("@abacus/common-utils");
var _constants = require("@/constants");
var _decimal = _interopRequireDefault(require("decimal.js"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const adjustmentSchema = exports.adjustmentSchema = _zod.z.object({
  accountId: _zod.z.coerce.number({
    required_error: "Account ID required",
    invalid_type_error: "Account ID is not valid"
  }).int({
    message: "Account ID is not valid"
  }).positive({
    message: "Account ID is not valid"
  }),
  accountName: _zod.z.string({
    message: "Account Name is not valid"
  }).optional(),
  activityMonth: _zod.z.coerce.number({
    required_error: "Activity month is required",
    invalid_type_error: "Activity month is not valid"
  }).int({
    message: "Activity month is not valid"
  }).min(1, {
    message: "Activity month must be a valid month number"
  }).max(12, {
    message: "Activity month must be a valid month number"
  }),
  activityYear: _zod.z.coerce.number({
    required_error: "Activity year is required",
    invalid_type_error: "Activity year is not valid"
  }).int({
    message: "Activity year is not valid"
  }).positive({
    message: "Activity year is not valid"
  }).min(1000, {
    message: "Activity year must be four digits"
  }).max(9999, {
    message: "Activity year must be four digits"
  }),
  adjustmentType: _zod.z.string({
    required_error: "Adjustment type is required",
    invalid_type_error: "Adjustment type is not valid"
  }).transform(val => val.toLowerCase()),
  amount: _zod.z.union([_zod.z.string(), _zod.z.number()], {
    required_error: "Amount is required",
    invalid_type_error: "Amount is not valid"
  }).transform((val, ctx) => {
    try {
      return new _decimal.default(val);
    } catch {
      ctx.addIssue({
        path: ["amount"],
        code: _zod.z.ZodIssueCode.custom,
        message: "Amount is not valid"
      });
      return new _decimal.default(0);
    }
  }).refine(val => !val.isZero(), {
    message: "Amount cannot be zero"
  }),
  clientFacingComments: _zod.z.string({
    message: "Client Facing Comments is not valid"
  }).max(180, "Client Facing Comments maximum length is 180 characters").optional(),
  contractId: _zod.z.coerce.number({
    required_error: "Contract ID is required",
    invalid_type_error: "Contract ID is not valid"
  }).int({
    message: "Contract ID is not valid"
  }).positive({
    message: "Contract ID is not valid"
  }),
  currency: _zod.z.string({
    required_error: "Currency code is required",
    invalid_type_error: "Currency code is not valid"
  }).transform((val, ctx) => {
    val = val.toUpperCase();
    if (!(0, _commonUtils.isAlpha)(val)) {
      ctx.addIssue({
        path: ["currency"],
        code: _zod.z.ZodIssueCode.custom,
        message: "Currency code is not valid"
      });
    } else if (!_constants.CURRENCIES.has(val)) {
      ctx.addIssue({
        path: ["currency"],
        code: _zod.z.ZodIssueCode.custom,
        message: "Currency code is not supported"
      });
    }
    return val;
  }),
  contractName: _zod.z.string({
    message: "Contract Name is not valid"
  }).optional(),
  distributionType: _zod.z.string({
    message: "Distribution type is not valid"
  }).transform((val, ctx) => {
    val = val.toLowerCase();
    if (!_constants.DISTRIBUTION_TYPES.has(val)) {
      ctx.addIssue({
        path: ["distributionType"],
        code: _zod.z.ZodIssueCode.custom,
        message: "Distribution type not supported"
      });
    }
    return val;
  }).optional(),
  internalNote: _zod.z.string({
    message: "Internal Note is not valid"
  }).max(1024, "Internal Note maximum length is 1024 characters").optional(),
  statementMonth: _zod.z.coerce.number({
    required_error: "Statement month is required",
    invalid_type_error: "Statement month is not valid"
  }).int({
    message: "Statement month is not valid"
  }).min(1, {
    message: "Statement month must be a valid month number"
  }).max(12, {
    message: "Statement month must be a valid month number"
  }),
  statementYear: _zod.z.coerce.number({
    required_error: "Statement year is required",
    invalid_type_error: "Statement year is not valid"
  }).int({
    message: "Statement year is not valid"
  }).positive({
    message: "Statement year is not valid"
  }).min(1000, {
    message: "Statement year must be four digits"
  }).max(9999, {
    message: "Statement year must be four digits"
  }),
  upc: _zod.z.coerce.string().transform((upc, ctx) => {
    upc = upc.replace(/\.0+$/, "");
    if (upc.length < 11 || upc.length > 13) {
      ctx.addIssue({
        path: ["upc"],
        code: _zod.z.ZodIssueCode.custom,
        message: "UPC must be 11-13 digits long"
      });
    } else if (!/^[0-9]+$/gi.test(upc)) {
      ctx.addIssue({
        path: ["upc"],
        code: _zod.z.ZodIssueCode.custom,
        message: "UPC must be a digit"
      });
    }
    return upc;
  }).optional()
}).superRefine((row, ctx) => {
  // Distribution Type + UPC
  if (row.distributionType != null && row.upc == null) {
    ctx.addIssue({
      path: ["distributionType"],
      code: _zod.z.ZodIssueCode.custom,
      message: "Distribution type must be blank if UPC is blank"
    });
  }

  // UPC + Distribution Type
  if (row.distributionType == null && row.upc != null) {
    ctx.addIssue({
      path: ["upc"],
      code: _zod.z.ZodIssueCode.custom,
      message: "UPC must be blank if distribution type is blank"
    });
  }
});